import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/feature/JsonLd';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useReaddyForm } from '@/hooks/useReaddyForm';
import { principles, testimonials, siteStats } from '@/mocks/site';

const CONTACT_ADDR = 'https://readdy.ai/api/form/dah9lt3oh653ivfvnp9g';

export default function AboutPage() {
  usePageMeta(
    '关于我们 - 猎奇吃瓜，一个认真讲离谱故事的栏目',
    '了解猎奇吃瓜的品牌定位、内容原则与用户口碑。奇葩人物、迷惑行为、沙雕新闻天天更新，欢迎爆料投稿。',
  );

  const contact = useReaddyForm({ submitAddr: CONTACT_ADDR, honeypotName: 'contact_alt' });

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于猎奇吃瓜',
    inLanguage: 'zh-CN',
    description: '猎奇吃瓜的品牌介绍、内容原则与联系方式。',
  };

  const contactSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: '联系猎奇吃瓜',
    inLanguage: 'zh-CN',
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <JsonLd data={aboutSchema} />
      <JsonLd data={contactSchema} />
      <SiteHeader />
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

      <main className="flex-1">
        <section className="w-full border-b border-background-200 bg-background-100 px-4 py-12 md:px-8 md:py-16">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-4 py-1.5 text-xs font-bold text-primary-700">
              <i className="ri-emotion-laugh-line" aria-hidden="true"></i>
              关于猎奇吃瓜
            </span>
            <h1 className="mt-5 font-heading text-3xl leading-snug text-foreground-950 md:text-5xl">
              把最离谱的事，讲得清清楚楚
            </h1>
            <p className="mt-5 text-base leading-relaxed text-foreground-600 md:text-lg">
              猎奇吃瓜是一个中文内容娱乐栏目，我们收集生活里那些让人忍不住"啊？"一声的真实场面，
              再把它整理成有起因、有经过、有结果的故事，让你笑着看完，也知道自己看了什么。
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/category/shadiao-xinwen"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
              >
                <i className="ri-newspaper-line" aria-hidden="true"></i>
                去看沙雕新闻
              </Link>
              <a
                href="#contact"
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-bold text-foreground-800 transition-colors hover:border-primary-300"
              >
                <i className="ri-send-plane-line" aria-hidden="true"></i>
                我要爆料
              </a>
            </div>
          </div>

          <dl className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
            {siteStats.map((stat) => (
              <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 px-4 py-4">
                <span className="flex items-center gap-1 text-xs text-foreground-500">
                  <i className={stat.icon} aria-hidden="true"></i>
                  {stat.label}
                </span>
                <dd className="mt-1 font-heading text-xl text-foreground-950 md:text-2xl">{stat.value}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="w-full px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div>
              <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">我们是谁</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                猎奇吃瓜由一群喜欢在聊天里分享"你今天看到那个了吗"的内容爱好者维护。我们不追热点标题，
                也不制造对立，只做一件事：把网上那些散落的奇葩人物、迷惑行为、沙雕新闻和离谱故事，
                汇集成一个可以安心刷、开心看的地方。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                我们相信，好笑和认真从来不是对立的。越是荒唐的场面，越值得被好好还原。
                每一篇内容都会交代清楚来龙去脉，而不是只给你一句抓耳的标题。
              </p>
            </div>
            <div>
              <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">我们的定位</h2>
              <ul className="mt-4 flex flex-col gap-3">
                {[
                  '面向碎片时间的内容栏目，通勤、午休、睡前都能轻松看完一篇。',
                  '六个固定栏目覆盖不同类型，找起来快，看起来顺。',
                  '不玩标题党，不用夸张标题骗点击，内容本身就是亮点。',
                  '欢迎读者投稿，优质的故事我们会核实后整理发布。',
                ].map((text) => (
                  <li key={text} className="flex gap-3 rounded-lg border border-background-200 bg-background-100/60 p-4">
                    <i className="ri-checkbox-circle-line mt-0.5 text-lg text-primary-600" aria-hidden="true"></i>
                    <span className="text-sm leading-relaxed text-foreground-700">{text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="w-full border-y border-background-200 bg-background-100 px-4 py-12 md:px-8 md:py-16">
          <div className="mb-8 text-center">
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">内容原则</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
              笑着吃瓜，也要吃得明白。这四条是我们一直在坚持的底线。
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-100 text-2xl text-accent-700">
                  <i className={item.icon} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full bg-secondary-950 px-4 py-12 md:px-8 md:py-16">
          <div className="mb-8 text-center">
            <h2 className="font-heading text-2xl text-white md:text-3xl">读者口碑</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-secondary-200">
              这些留言来自每天准点来蹲更新的瓜友。
            </p>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((item) => (
              <figure key={item.name} className="flex flex-col rounded-lg bg-secondary-900 p-6">
                <i className="ri-chat-quote-line text-3xl text-accent-400" aria-hidden="true"></i>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-secondary-100">
                  {item.text}
                </blockquote>
                <figcaption className="mt-5 border-t border-secondary-700 pt-4">
                  <span className="text-sm font-bold text-white">{item.name}</span>
                  <span className="ml-2 text-xs text-secondary-300">{item.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section id="contact" className="w-full scroll-mt-24 bg-background-50 px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">联系我们</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                看到过特别离谱的故事？想把身边的名场面分享给更多人？直接在右边的表单里留言，
                我们会认真阅读每一条投稿，合适的会整理后发布。
              </p>
              <ul className="mt-6 flex flex-col gap-3">
                <li className="flex items-center gap-3 text-sm text-foreground-700">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-700">
                    <i className="ri-mail-line" aria-hidden="true"></i>
                  </span>
                  hello@lieqi-chigua.example.com
                </li>
                <li className="flex items-center gap-3 text-sm text-foreground-700">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-xl text-accent-700">
                    <i className="ri-time-line" aria-hidden="true"></i>
                  </span>
                  投稿处理时间：通常在 3 个工作日内
                </li>
                <li className="flex items-center gap-3 text-sm text-foreground-700">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-xl text-secondary-700">
                    <i className="ri-shield-check-line" aria-hidden="true"></i>
                  </span>
                  投稿内容仅用于栏目整理，不会用于其他用途
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100/60 p-6 md:p-8">
              <h3 className="font-heading text-xl text-foreground-950">爆料投稿表单</h3>
              <form
                data-readdy-form
                className="mt-5 flex flex-col gap-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  contact.submit(e.currentTarget);
                }}
              >
                <div className="flex flex-col gap-2">
                  <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
                    你的称呼
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="比如：吃瓜的老王"
                    autoComplete="name"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
                    邮箱地址
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="方便我们回复你"
                    autoComplete="email"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="contact-type" className="text-sm font-medium text-foreground-800">
                    投稿类型
                  </label>
                  <select
                    id="contact-type"
                    name="type"
                    required
                    defaultValue=""
                    className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400"
                  >
                    <option value="" disabled>
                      请选择投稿类型
                    </option>
                    <option value="迷惑行为">迷惑行为</option>
                    <option value="沙雕新闻">沙雕新闻</option>
                    <option value="离谱故事">离谱故事</option>
                    <option value="搞笑怪事">搞笑怪事</option>
                    <option value="魔幻现实">魔幻现实</option>
                    <option value="合作与建议">合作与建议</option>
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">
                    故事的来龙去脉
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="尽量写清楚时间、地点和经过，方便我们核实整理（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                  ></textarea>
                </div>

                <input
                  className="gq-deco-field"
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={contact.status === 'submitting'}
                  className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-foreground-950"
                >
                  <i className="ri-send-plane-line" aria-hidden="true"></i>
                  {contact.status === 'submitting' ? '提交中…' : '提交爆料'}
                </button>

                {contact.status === 'success' && (
                  <p className="rounded-md bg-secondary-100 px-3 py-2 text-sm text-secondary-900">
                    收到啦，感谢分享！我们会尽快阅读你的故事。
                  </p>
                )}
                {contact.status === 'error' && (
                  <p className="rounded-md bg-primary-100 px-3 py-2 text-sm text-primary-800">
                    {contact.formError}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}